import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchTeamMembers, fetchSession } from "../lib/api-client";
import type { TeamMember } from "../types/api";

function TeamMembersPage(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();

  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
  });

  const membersQuery = useQuery({
    queryKey: ["team-members", teamId],
    queryFn: () => fetchTeamMembers(teamId!),
    enabled: Boolean(teamId),
  });

  // Check if current user is SuperAdmin
  const currentUserRoles = sessionQuery.data?.session?.roles ?? [];
  const isSuperAdmin = currentUserRoles.includes("SuperAdmin");

  // Filter out SuperAdmin members if current user is not SuperAdmin
  const members = membersQuery.data?.members ?? [];
  const filteredMembers = isSuperAdmin
    ? members
    : members.filter((member) => member.globalRole !== "SuperAdmin");

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Team Members</h1>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            View and manage members of this team.
          </p>
        </div>
      </header>

      <main style={{ padding: "2rem", flex: 1 }}>
        <section className="surface" style={{ padding: "1.5rem" }}>
          <MembersTable members={filteredMembers} isLoading={membersQuery.isLoading} />
        </section>
      </main>
    </div>
  );
}

function MembersTable({
  members,
  isLoading,
}: {
  members: TeamMember[];
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) {
    return <div style={{ color: "var(--color-text-muted)" }}>Loading members...</div>;
  }
  if (members.length === 0) {
    return <div style={{ color: "var(--color-text-muted)" }}>No team members yet.</div>;
  }
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Role</th>
          <th>Status</th>
          <th>Joined</th>
        </tr>
      </thead>
      <tbody>
        {members.map((member) => (
          <tr key={member.id}>
            <td>{member.userId}</td>
            <td>{member.email ?? "—"}</td>
            <td>{member.role}</td>
            <td>{member.status}</td>
            <td>{member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : "Pending"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default TeamMembersPage;

